<script lang="ts" setup>
  import Button from '../Buttons/Button.vue'

  const { t } = useI18n()
  export type ArticleSummaryProps = {
    articleImage: string
    articleUrl: string
    context: string
    topicBullets: string[]
    design?: 'google' | 'msn'
    source?: string
  }

  type ArticleSummaryEmits = {
    originalNewsButtonClick: []
  }

  const props = withDefaults(defineProps<ArticleSummaryProps>(), {
    design: 'google',
    source: '',
  })
  const emit = defineEmits<ArticleSummaryEmits>()

  const adToggles = reactive<Record<string, boolean>>({
    showArticleImage: true,
    showContentTopAdPlacement: false,
    showContentMiddleAdPlacement: true,
    showContentAboveButtonAdPlacement: false,
    showContentBelowButtonAdPlacement: true,
    showContentBelowImageAdPlacement: false,
  })

  onBeforeMount(async () => {
    const { client } = await useOpenFeature()

    for (const toggle of Object.keys(adToggles)) {
      adToggles[toggle] = await client.getBooleanValue(
        toggle,
        adToggles[toggle]
      )
    }
  })

  const handleOriginalNewsButtonClick = () => {
    window.open(props.articleUrl, '_blank')
    emit('originalNewsButtonClick')
  }

  const adPrefix = useAppConfig().siteAdPrefix
</script>
<template>
  <div
    :class="{
      'flex flex-col items-center gap-6 p-8 bg-background-default rounded-xl':
        design === 'google',
      'flex flex-col items-center gap-6 py-6': design === 'msn',
    }"
  >
    <div
      v-if="adToggles.showContentTopAdPlacement"
      :class="`${adPrefix}_article_content_top`"
      class="ad-placement"
    />
    <NuxtImg
      v-if="adToggles.showArticleImage"
      :src="props.articleImage"
      alt="Article Image"
      class="h-[432px] w-full rounded-xl object-cover"
    />
    <div
      v-if="adToggles.showContentBelowImageAdPlacement"
      :class="`${adPrefix}_article_content_below_image`"
      class="ad-placement"
    />
    <div class="flex flex-col items-center gap-3 self-stretch">
      <Typography
        v-if="source !== 'canadian_press'"
        class="text-content-primary self-stretch"
        element="h2"
        variant="text-xl"
        weight="font-bold"
      >
        {{ t('Context') }}:
      </Typography>
      <Typography
        v-for="(paragraph, i) in props.context.split('\n')"
        :key="i"
        class="text-content-primary"
        variant="text-lg"
      >
        {{ paragraph }}
      </Typography>
      <div
        v-if="adToggles.showContentMiddleAdPlacement"
        :class="`${adPrefix}_article_content_middle`"
        class="ad-placement"
      />
    </div>
    <div
      v-if="props.topicBullets.some((b) => b.trim() !== '')"
      class="flex flex-col items-center gap-3 self-stretch"
    >
      <Typography
        class="text-content-primary self-stretch"
        element="h2"
        variant="text-xl"
        weight="font-bold"
      >
        {{ t('Dive Deeper') }}:
      </Typography>
      <ul
        class="text-content-primary flex list-disc flex-col items-start gap-6 self-stretch pl-7"
      >
        <li v-for="bullet in props.topicBullets" :key="bullet">
          <Typography class="text-content-primary" variant="text-lg">
            {{ bullet }}
          </Typography>
        </li>
      </ul>
    </div>
    <div
      v-if="adToggles.showContentAboveButtonAdPlacement"
      :class="`${adPrefix}_article_content_above_button`"
      class="ad-placement"
    />
    <Button
      v-if="source !== 'canadian_press' && !!props.articleUrl"
      size="large"
      class="mb-4"
      @click="handleOriginalNewsButtonClick"
    >
      {{ t('Read original article') }}
    </Button>
    <div
      v-if="adToggles.showContentBelowButtonAdPlacement"
      :class="`${adPrefix}_article_content_below_button`"
      class="ad-placement"
    />
  </div>
</template>
